import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { motion } from 'framer-motion';
import { BookOpenCheck, Scale, HardHat, GraduationCap, Presentation, PenTool, BriefcaseBusiness, Utensils, Film, Leaf } from 'lucide-react';
import PromotionalBanner from '@/components/PromotionalBanner';
import Gradient from '@/assets/medihub_gradient_mesh.jpg';

interface IndustryCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  benefits: string[];
  accentColor?: string;
}

const IndustryCard: React.FC<IndustryCardProps> = ({ 
  title, 
  description, 
  icon, 
  benefits,
  accentColor = "bg-medihub-accent"
}) => {
  return (
    <Card className="h-full border-t-4 border-t-medihub-accent overflow-hidden">
      <CardContent className="p-6">
        <div className="flex flex-col h-full">
          <div className="mb-4">
            <div className={`w-12 h-12 rounded-full ${accentColor} flex items-center justify-center text-white mb-4`}>
              {icon}
            </div>
            <h3 className="text-xl font-bold mb-2">{title}</h3>
            <p className="text-gray-600 mb-4">{description}</p>
          </div>
          
          <div className="mt-auto">
            <h4 className="font-semibold text-sm text-gray-500 mb-2">ELŐNYÖK</h4>
            <ul className="space-y-1">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-2">
                  <div className="w-5 h-5 mt-0.5 rounded-full bg-medihub-primary/10 flex items-center justify-center flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-medihub-primary"></div>
                  </div>
                  <span className="text-sm text-gray-700">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const IndustrySelectionSection = () => {
  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-2">Szakmai területek</Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Specializált megoldások minden szakterülethez</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Naptárrendszerünk rugalmasan alkalmazkodik a különböző szakmai területek egyedi igényeihez. 
            Fedezze fel, hogyan segíthet az Ön szakterületén!
          </p>
        </div>

        <Tabs defaultValue="jog" className="w-full">
          <TabsList className="flex flex-wrap justify-center bg-transparent h-auto p-0 mb-8">
            <TabsTrigger value="jog" className="data-[state=active]:bg-medihub-primary/10 data-[state=active]:text-medihub-primary rounded-full px-4 py-2 m-1">
              Jogi és pénzügyi
            </TabsTrigger>
            <TabsTrigger value="muszaki" className="data-[state=active]:bg-medihub-primary/10 data-[state=active]:text-medihub-primary rounded-full px-4 py-2 m-1">
              Mérnöki és műszaki
            </TabsTrigger>
            <TabsTrigger value="oktatas" className="data-[state=active]:bg-medihub-primary/10 data-[state=active]:text-medihub-primary rounded-full px-4 py-2 m-1">
              Oktatás és tudomány
            </TabsTrigger>
            <TabsTrigger value="kreativ" className="data-[state=active]:bg-medihub-primary/10 data-[state=active]:text-medihub-primary rounded-full px-4 py-2 m-1">
              Kreatív szakmák
            </TabsTrigger>
            <TabsTrigger value="egyeb" className="data-[state=active]:bg-medihub-primary/10 data-[state=active]:text-medihub-primary rounded-full px-4 py-2 m-1">
              További területek
            </TabsTrigger>
          </TabsList>

          <TabsContent value="jog" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <IndustryCard
                title="Jogász közösségek"
                description="Ügyvédi kamarák, jogász szövetségek és jogi oktatási intézmények számára optimalizált eseménykezelés"
                icon={<Scale className="h-6 w-6" />}
                benefits={[
                  "Kredit-követés ügyvédi továbbképzésekhez",
                  "Jogi konferenciák speciális regisztrációs igényei",
                  "Előadók és panelbeszélgetések kezelése",
                  "Szakmai szekciók és párhuzamos programok szervezése"
                ]}
              />
              
              <IndustryCard
                title="Könyvelők, adótanácsadók"
                description="Adószakértők, könyvelők és pénzügyi tanácsadók eseménykezelési megoldása"
                icon={<BriefcaseBusiness className="h-6 w-6" />}
                benefits={[
                  "Adóváltozásokkal kapcsolatos workshopok kezelése",
                  "Szakmai továbbképzési pontok nyilvántartása",
                  "Regisztrációk és számlázás automatizálása",
                  "Online és hibrid események támogatása"
                ]}
              />
              
              <div className="md:col-span-2 lg:col-span-1">
                <PromotionalBanner
                  title="Pénzügyi szakemberek képzéseinek optimalizálása"
                  description="Hatékony eszköz bankárok, pénzügyi tanácsadók és befektetési szakemberek részére"
                  benefits={[
                    "Kötelező szakmai továbbképzések követése",
                    "Komplex regisztrációs folyamatok egyszerűsítése",
                    "Előadók és résztvevők hatékony kezelése",
                    "Automatikus emlékeztetők és visszaigazolások"
                  ]}
                  backgroundImage={Gradient}
                  size="lg"
                  ctaText="Kérjen egyedi ajánlatot"
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="muszaki" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <IndustryCard
                title="Mérnöki kamarák"
                description="Mérnöki kamarák és szakmai szervezetek számára fejlesztett eseménykezelési rendszer"
                icon={<HardHat className="h-6 w-6" />}
                benefits={[
                  "Mérnöki továbbképzési pontok nyilvántartása",
                  "Szakmai konferenciák regisztrációs folyamatai",
                  "Tematikus szekciók és workshop-ok kezelése",
                  "Technikai dokumentáció és anyagok megosztása"
                ]}
              />
              
              <IndustryCard
                title="IT szakemberek"
                description="Informatikai konferenciák, meetup-ok és workshopok szervezésére optimalizálva"
                icon={<Presentation className="h-6 w-6" />}
                benefits={[
                  "Komplex eseménystruktúra kezelése (előadások, workshopok)",
                  "Előadói profilok és anyagok megosztása",
                  "Automatikus visszaigazolások és emlékeztetők",
                  "Integráció népszerű fejlesztői platformokkal"
                ]}
              />
              
              <div className="md:col-span-2 lg:col-span-1">
                <PromotionalBanner
                  title="Műszaki továbbképzések menedzsmentje"
                  description="Tökéletes megoldás építészek, kivitelezők és ipari szakemberek számára"
                  benefits={[
                    "Szakági képzések specializált kezelése",
                    "Szabványos és egyedi jelentkezési folyamatok",
                    "Jogosultságok és minősítések nyilvántartása",
                    "Integrált partneri hálózat és promotálás"
                  ]}
                  backgroundImage={Gradient}
                  size="lg"
                  ctaText="Fedezze fel a lehetőségeket"
                />
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="oktatas" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <IndustryCard
                title="Felsőoktatási intézmények"
                description="Egyetemek és főiskolák konferenciáinak, workshopjainak menedzsmentje"
                icon={<GraduationCap className="h-6 w-6" />}
                benefits={[
                  "Tudományos konferenciák komplex strukturálása",
                  "Absztraktok és publikációk kezelése",
                  "Párhuzamos szekciók és előadások szervezése",
                  "Nemzetközi résztvevők és többnyelvűség támogatása"
                ]}
              />
              
              <IndustryCard
                title="Tudományos társaságok"
                description="Tudományos kutatók és szakmai társaságok specializált rendezvénykezelése"
                icon={<BookOpenCheck className="h-6 w-6" />}
                benefits={[
                  "Peer review folyamatok integrálása",
                  "Publikációs és előadási anyagok kezelése",
                  "Szakmai közösségek építése",
                  "Tudománymetriai adatok és hivatkozások követése"
                ]}
              />
              
              <div className="md:col-span-2 lg:col-span-1">
                <PromotionalBanner
                  title="Pedagógus továbbképzések menedzsmentje"
                  description="Specializált megoldás oktatási intézmények és továbbképző központok számára"
                  benefits={[
                    "Akkreditált képzések pontrendszerének követése",
                    "Pedagógus csoportok specializált kezelése",
                    "Oktatási segédanyagok integrált megosztása",
                    "Visszajelzési és értékelési rendszer"
                  ]}
                  backgroundImage={Gradient}
                  size="lg"
                  ctaText="Kérje egyedi megoldásunkat"
                />
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="kreativ" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <IndustryCard
                title="Kreatív iparágak"
                description="Designerek, művészek és kreatív szakemberek eseményeinek kezelése"
                icon={<PenTool className="h-6 w-6" />}
                benefits={[
                  "Portfólió bemutatók és kiállítások szervezése",
                  "Workshopok és mesterkurzusok menedzsmentje",
                  "Vizuális anyagok megosztása és bemutatása",
                  "Networking események és kreatív találkozók"
                ]}
              />
              
              <IndustryCard
                title="Film és média"
                description="Filmfesztiválok, szakmai vetítések és média események kezelése"
                icon={<Film className="h-6 w-6" />}
                benefits={[
                  "Komplex programok és vetítési menetrendek",
                  "Jegyértékesítés és helyfoglalási rendszer",
                  "Zsűritagok és meghívott vendégek kezelése",
                  "Sajtóakkreditáció és médiakapcsolatok"
                ]}
              />
              
              <div className="md:col-span-2 lg:col-span-1">
                <PromotionalBanner
                  title="Gasztronómiai események szervezése"
                  description="Szakácsok, vendéglátósok és gasztro-események specializált kezelése"
                  benefits={[
                    "Food tasting és kóstolók menedzsmentje",
                    "Chef találkozók és gasztro-konferenciák",
                    "Speciális étrendek és allergének kezelése",
                    "Gasztroturizmus és vendéglátó események"
                  ]}
                  backgroundImage={Gradient}
                  size="lg"
                  ctaText="Fedezze fel a lehetőségeket"
                />
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="egyeb" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <IndustryCard
                title="Agrár- és élelmiszergazdaság"
                description="Mezőgazdasági szakemberek, termelők és élelmiszeripari szakértők számára"
                icon={<Leaf className="h-6 w-6" />}
                benefits={[
                  "Szezonális szakmai rendezvények kezelése",
                  "Szakmai bemutatók és tereplátogatások szervezése",
                  "Agrárkamarai képzések nyilvántartása",
                  "Területspecifikus szakmai közösségek építése"
                ]}
              />
              
              <div className="md:col-span-2 lg:col-span-2">
                <PromotionalBanner
                  title="Nem találja az Ön területét? Egyedi megoldást kínálunk!"
                  description="Bármilyen szakmai terület egyedi igényeihez alkalmazkodni tudunk. Forduljon hozzánk bizalommal!"
                  benefits={[
                    "Teljesen testreszabható megoldások minden szakterülethez",
                    "Egyedi folyamatok és jelentkezési rendszerek kialakítása",
                    "Szakmaspecifikus funkciók fejlesztése",
                    "Meglévő rendszerekkel való integráció",
                    "Személyre szabott tanácsadás és implementáció"
                  ]}
                  backgroundImage={Gradient}
                  size="lg"
                  ctaText="Vegye fel velünk a kapcsolatot"
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default IndustrySelectionSection;
